<script lang="ts" setup>
    import { useQuasar } from 'quasar'

    const props = defineProps<{
        node: {
            id: string
            name: string
            mimeType: string
            isFolder: boolean
            children: any[]
            loading: boolean
            loaded: boolean
        }
        depth: number
        selectedId: string | null
        showFiles: boolean
        forceExpandIds?: string[]
    }>()

    const emit = defineEmits<{
        (e: 'select', node: any): void
        (e: 'expand', node: any): void
    }>()

    const $q = useQuasar()
    const FOLDER_MIME = 'application/vnd.google-apps.folder'

    const isExpanded = ref(false)

    watch(() => props.forceExpandIds, (ids) => {
        if (ids?.includes(props.node.id) && !isExpanded.value) {
            isExpanded.value = true
            if (!props.node.loaded) {
                emit('expand', props.node)
            }
        }
    }, { immediate: true })

    const selectedRowClass = computed(() => {
        if (props.node.id !== props.selectedId) {
            return ''
        }
        return $q.dark.isActive ? 'bg-blue-9 text-white' : 'bg-blue-1 text-primary'
    })

    function fileIcon(mimeType: string): string {
        if (mimeType === FOLDER_MIME) {
            return 'folder'
        }
        if (mimeType.startsWith('image/')) {
            return 'image'
        }
        if (mimeType === 'application/pdf') {
            return 'picture_as_pdf'
        }
        if (mimeType.startsWith('video/')) {
            return 'videocam'
        }
        return 'insert_drive_file'
    }

    function toggleExpand() {
        if (!props.node.isFolder) {
            return
        }
        if (!isExpanded.value && !props.node.loaded) {
            emit('expand', props.node)
        }
        isExpanded.value = !isExpanded.value
    }
</script>

<template>
    <div>
        <div
            class="row items-center q-py-xs cursor-pointer"
            :class="selectedRowClass"
            :style="{ paddingLeft: `${depth * 16 + 8}px` }"
            @click="$emit('select', node)"
        >
            <q-btn
                v-if="node.isFolder"
                :icon="isExpanded ? 'expand_more' : 'chevron_right'"
                flat
                round
                dense
                size="xs"
                @click.stop="toggleExpand"
            />
            <div
                v-else
                style="width: 28px"
            />

            <q-icon
                :name="fileIcon(node.mimeType)"
                :color="node.isFolder ? 'amber-8' : 'grey-7'"
                size="20px"
                class="q-mr-sm"
            />
            <span class="text-body2">{{ node.name }}</span>

            <q-spinner
                v-if="node.loading"
                class="q-ml-sm"
                color="primary"
                size="16px"
            />
        </div>

        <template v-if="isExpanded && node.loaded">
            <google-drive-picker-node
                v-for="child in node.children"
                :key="child.id"
                :node="child"
                :depth="depth + 1"
                :selected-id="selectedId"
                :show-files="showFiles"
                :force-expand-ids="forceExpandIds"
                @select="$emit('select', $event)"
                @expand="$emit('expand', $event)"
            />
        </template>
    </div>
</template>

<style scoped></style>
